import { useRef, useState } from "react";
import { Card } from "@/components/ui";
import { Icon } from "@/components/icon/Icon";
import { Reveal } from "@/components/motion/Reveal";
import { track } from "@/lib/track";
import { usePortfolio } from "@/lib/portfolio";
import { Flip, gsap, MOTION_OK, ScrollTrigger, useGSAP } from "@/lib/gsap";
import {
  filterPortfolio,
  PORTFOLIO_FILTERS,
  type PortfolioFilterKey,
} from "@/lib/portfolio-filter";

const PLATFORM_LABEL: Record<string, string> = { web: "웹", app: "앱" };
const CARD_SELECTOR = "[data-card]";

export function Portfolio() {
  // 목록의 원본은 DB(어드민 → 포트폴리오 탭)예요. 못 받아오면 폴백 스냅샷으로 그려요.
  const portfolio = usePortfolio();
  const [filter, setFilter] = useState<PortfolioFilterKey>("all");
  const visibleIds = new Set(filterPortfolio(portfolio, filter).map((item) => item.id));

  const gridRef = useRef<HTMLDivElement>(null);
  // 상태를 바꾸기 **직전**의 카드 배치. React 가 DOM 을 고친 뒤 여기서부터 되짚어 움직여요.
  const before = useRef<Flip.FlipState | null>(null);

  /**
   * 필터를 바꾸면 카드가 격자에서 자리를 옮겨요. 그냥 두면 순간이동처럼 튀는데,
   * FLIP 은 바뀌기 전 좌표를 기억했다가 새 자리에서 옛 자리로 되돌린 뒤 풀어줘요.
   * 걸러진 카드는 언마운트하지 않고 `hidden` 으로만 숨겨요 — 사라지는 순간을 애니메이션하려면
   * 그 노드가 아직 DOM 에 있어야 하거든요.
   */
  const withFlip = (change: () => void) => {
    const grid = gridRef.current;
    if (grid) before.current = Flip.getState(grid.querySelectorAll(CARD_SELECTOR));
    change();
  };

  useGSAP(
    () => {
      const state = before.current;
      if (!state) return;
      before.current = null;

      // 동작 줄이기면 FLIP 없이 새 배치로 바로 앉아요.
      if (!window.matchMedia(MOTION_OK).matches) return;

      Flip.from(state, {
        duration: 0.45,
        ease: "power2.inOut",
        absolute: true,
        nested: true,
        onEnter: (els) =>
          gsap.fromTo(els, { opacity: 0, scale: 0.96 }, { opacity: 1, scale: 1, duration: 0.3 }),
        onLeave: (els) => gsap.to(els, { opacity: 0, scale: 0.96, duration: 0.2 }),
      });
    },
    { dependencies: [filter], scope: gridRef }
  );

  // 첫 진입: 화면에 들어오는 카드들이 순서대로 올라와요.
  useGSAP(
    () => {
      const grid = gridRef.current;
      if (!grid) return;
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        ScrollTrigger.batch(gsap.utils.toArray<HTMLElement>(CARD_SELECTOR, grid), {
          start: "top 88%",
          once: true,
          onEnter: (batch) =>
            gsap.from(batch, {
              opacity: 0,
              y: 24,
              duration: 0.5,
              stagger: 0.06,
              ease: "power3.out",
              overwrite: true,
            }),
        });
      });
    },
    { scope: gridRef }
  );

  return (
    <section id="portfolio" className="scroll-mt-20 px-6 py-24">
      <div className="mx-auto flex max-w-layout flex-col gap-10">
        <Reveal className="flex flex-col gap-3">
          <span aria-hidden="true" className="h-1 w-10 rounded-full bg-primary" />
          <h2 className="wds-title-1 text-label-strong">직접 만들어 운영하는 서비스들</h2>
          <p className="wds-body-1-reading text-label-neutral">
            디체인커넥트가 직접 기획하고 만든 자사 제품이에요. 대부분 운영 중이고, 출시를 준비하는
            것도 있어요.{" "}
            <span className="font-semibold text-label-strong">
              고객사 프로젝트도 이 팀이 그대로 맡아요
            </span>{" "}
            — 여기 보이는 수준으로 만들고, 원하시면 운영까지 이어서 해드려요.
          </p>
          {/* 왜 30개+ 실적이 여기 다 안 보이는지 설명하는 자리예요 — 없으면 자사 제품 9개가 전부로 읽혀요. */}
          <p className="wds-caption-1 text-label-assistive">
            고객사 프로젝트는 계약상 비공개인 경우가 많아, 공개에 동의해 주신 서비스만 이곳에 싣고
            있어요.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          {/* biome-ignore lint/a11y/useSemanticElements: toggle-button toolbar, not a form control group — <fieldset> doesn't fit */}
          <div className="flex flex-wrap gap-2" role="group" aria-label="포트폴리오 필터">
            {PORTFOLIO_FILTERS.map((f) => {
              const active = filter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => withFlip(() => setFilter(f.key))}
                  className={`wds-label-1 rounded-full px-4 py-2 transition-colors duration-200 ease-standard ${
                    active
                      ? "bg-primary text-static-white"
                      : "bg-background-alternative text-label-neutral hover:bg-fill-normal"
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </Reveal>

        <div ref={gridRef} className="grid gap-3 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
          {portfolio.map((item) => (
            <div
              key={item.id}
              data-card=""
              data-flip-id={item.id}
              className={visibleIds.has(item.id) ? "" : "hidden"}
            >
              <Card variant="outline" className="flex h-full flex-col gap-3">
                {/*
                  모바일에서는 커버를 아예 감춰요 — 16:9 커버가 화면 폭을 다 먹으면 카드 하나가
                  거의 한 화면이라 9개를 훑는 스크롤이 너무 길어져요. 제품 식별은 로고와 이름이
                  하고, 커버는 sm 이상에서 원래대로 위에 깔려요.
                */}
                {item.cover && (
                  <div className="hidden overflow-hidden rounded-md bg-background-alternative sm:block">
                    <img
                      src={item.cover}
                      alt=""
                      loading="lazy"
                      className="aspect-video w-full object-cover"
                    />
                  </div>
                )}
                <div className="flex min-w-0 items-center gap-2">
                  {item.logo && (
                    <img
                      src={item.logo}
                      alt=""
                      loading="lazy"
                      className="size-6 shrink-0 rounded-md object-contain"
                    />
                  )}
                  <h3 className="wds-headline-1 truncate text-label-strong">{item.name}</h3>
                  {/* 이름 옆 아이콘 하나가 사이트 링크예요. 아이콘만 있으니 이름은 aria-label 이 대신해요. */}
                  {item.url && (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => track("portfolio_link", { detail: item.id })}
                      aria-label={`${item.name} 사이트를 새 탭에서 열기`}
                      title="사이트 방문하기"
                      className="-m-1 shrink-0 p-1 text-label-assistive transition-colors duration-200 ease-standard hover:text-primary"
                    >
                      <Icon name="arrowUpRight" size={18} aria-hidden="true" />
                    </a>
                  )}
                </div>
                <p className="wds-body-2-reading text-label-neutral">{item.tagline}</p>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {item.platform.map((p) => (
                    <span
                      key={p}
                      className="wds-caption-1 rounded-full bg-background-alternative px-2.5 py-0.5 text-label-assistive sm:px-3 sm:py-1"
                    >
                      {PLATFORM_LABEL[p] ?? p}
                    </span>
                  ))}
                </div>
              </Card>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Portfolio;
